import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  BookOpen,
  ClipboardList,
  History,
  Pencil,
  Play,
  Plus,
  QrCode,
  Tag,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS, newId } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Callout,
  DetailRow,
  EmptyState,
  ListRow,
  Modal,
  Spinner,
  Stat,
  StatusPill,
  complianceTone,
  oplStatusTone,
  problemStatusTone,
  runStatusTone,
  severityTone,
} from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth, useCan } from '../../lib/auth';
import { currentShift, deviceInfo, formatDate, formatDateTime, humanise } from '../../lib/format';
import { EquipmentFormModal } from './EquipmentFormModal';
import { ComponentsCard } from './ComponentsCard';

interface Machine {
  id: string;
  code: string;
  name: string;
  machineType: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNo: string | null;
  location: string | null;
  status: string;
  criticality: string;
  commissionDate: string | null;
  description: string | null;
  lineId: string;
  lineName: string;
  areaName: string;
  factoryName: string;
}

interface DueItem {
  scheduleId: string;
  templateId: string;
  templateCode: string;
  templateName: string;
  machineId: string;
  dueAt: string;
  status: string;
  runId: string | null;
}

interface History {
  machine: Machine;
  windowDays: number;
  compliance: {
    runsStarted: number;
    runsSubmitted: number;
    runsVerified: number;
    completionRate: number | null;
    abnormalities: number;
  };
  problems: { total: number; open: number; closed: number; criticalOpen: number };
  recurringIssues: Array<{
    componentId: string | null;
    componentName: string;
    occurrences: number;
    lastSeen: string;
  }>;
  recentRuns: Array<{
    id: string;
    status: string;
    startedAt: string | null;
    abnormalCount: number;
    problemCount: number;
    templateName: string;
    operatorName: string;
    shift: string | null;
  }>;
  recentProblems: Array<{
    id: string;
    tagNo: string;
    title: string;
    severity: string;
    status: string;
    detectedAt: string;
    componentName: string | null;
  }>;
  opls: Array<{ id: string; oplNo: string; title: string; oplType: string; status: string }>;
}

/** The screen a QR scan lands on (directive §10). */
export function MachineDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const queryClient = useQueryClient();
  const canExecute = useCan(PERMISSIONS.RUN_EXECUTE);
  const canManage = useCan(PERMISSIONS.MACHINE_MANAGE);
  const [qrOpen, setQrOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);

  const retire = useMutation({
    mutationFn: () => api.post(`/api/machines/${id}/retire`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setRetireOpen(false);
    },
  });

  const { data: machine, isLoading } = useQuery({
    queryKey: ['machine', id],
    queryFn: () => api.get<Machine>(`/api/machines/${id}`),
  });

  const { data: due } = useQuery({
    queryKey: ['due', id],
    queryFn: () => api.get<{ data: DueItem[] }>(`/api/dashboard/due?machineId=${id}`),
    enabled: Boolean(id),
  });

  const { data: history } = useQuery({
    queryKey: ['machine', id, 'history'],
    queryFn: () => api.get<History>(`/api/machines/${id}/history`),
    enabled: Boolean(id),
  });

  const startRun = useMutation({
    mutationFn: (item: DueItem) =>
      api.post<{ run: { id: string } }>('/api/checklist-runs', {
        id: newId(),
        templateId: item.templateId,
        machineId: id,
        scheduleId: item.scheduleId,
        shift: user?.shift ?? currentShift(),
        dueDate: item.dueAt,
        deviceInfo: deviceInfo(),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['due'] });
      navigate(`/checklists/${result.run.id}`);
    },
  });

  if (isLoading || !machine) return <Spinner label="Loading machine" />;

  const dueItems = due?.data ?? [];

  return (
    <div>
      <PageHeader
        title={`${machine.code} — ${machine.name}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span>
              {machine.factoryName} · {machine.areaName} · {machine.lineName}
            </span>
            <StatusPill
              tone={machine.status === 'RUNNING' ? 'ok' : machine.status === 'MAINTENANCE' ? 'warn' : 'neutral'}
              label={humanise(machine.status)}
            />
            <StatusPill tone={severityTone(machine.criticality)} label={`${humanise(machine.criticality)} criticality`} />
          </span>
        }
        actions={
          <>
            {canManage ? (
              <>
                <Button variant="secondary" icon={Pencil} onClick={() => setEditOpen(true)}>
                  Edit
                </Button>
                <Button variant="secondary" icon={QrCode} onClick={() => setQrOpen(true)}>
                  QR label
                </Button>
                {machine.status !== 'RETIRED' ? (
                  <Button variant="secondary" icon={Archive} onClick={() => setRetireOpen(true)}>
                    Retire
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        }
      />

      <div className="space-y-4 p-4 lg:p-6">
        {machine.status === 'RETIRED' ? (
          <Callout tone="warn" title="This equipment is retired">
            Its schedules are switched off, so nothing new is due. Every checklist run,
            Problem Tag and OPL it collected is kept — retiring never deletes history.
          </Callout>
        ) : null}

        {history && history.problems.criticalOpen > 0 ? (
          <Callout tone="alarm" title={`${history.problems.criticalOpen} critical problem(s) open on this machine`}>
            Check these before running production.
          </Callout>
        ) : null}

        {/* Big operator actions — the point of scanning the label. */}
        <Card>
          <CardHeader title="What do you want to do?" />
          <div className="grid gap-2 p-4 sm:grid-cols-2">
            {canExecute && dueItems.length > 0
              ? dueItems.map((item) => (
                  <Button
                    key={`${item.scheduleId}-${item.dueAt}`}
                    size="lg"
                    icon={Play}
                    className="justify-start"
                    loading={startRun.isPending && startRun.variables?.scheduleId === item.scheduleId}
                    onClick={() =>
                      item.runId ? navigate(`/checklists/${item.runId}`) : startRun.mutate(item)
                    }
                  >
                    <span className="text-left">
                      {item.runId ? 'Continue' : 'Start'} {item.templateName}
                      <span className="block text-xs font-normal opacity-80">
                        {humanise(item.status)}
                      </span>
                    </span>
                  </Button>
                ))
              : null}

            <Button
              variant="secondary"
              size="lg"
              icon={History}
              className="justify-start"
              onClick={() => navigate(`/machines/${id}/history`)}
            >
              Full machine history
              <span className="block text-xs font-normal opacity-80">
                Checks, problems, OPLs and standards on one timeline
              </span>
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={ClipboardList}
              className="justify-start"
              onClick={() => navigate(`/checklists?machineId=${id}`)}
            >
              Checklist history
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={BookOpen}
              className="justify-start"
              onClick={() => navigate(`/opl?machineId=${id}`)}
            >
              One-Point Lessons ({history?.opls.length ?? 0})
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={Tag}
              className="justify-start"
              onClick={() => navigate(`/problems?machineId=${id}&openOnly=1`)}
            >
              Problem tags ({history?.problems.open ?? 0} open)
            </Button>
          </div>
          {canExecute && dueItems.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-steel-500">
              No CILT check is due on this machine right now.
            </p>
          ) : null}
        </Card>

        {history ? (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat
                label="CILT completion"
                value={history.compliance.completionRate === null ? '—' : `${history.compliance.completionRate}%`}
                hint={`${history.compliance.runsSubmitted} of ${history.compliance.runsStarted} started in ${history.windowDays} days`}
                tone={
                  history.compliance.completionRate === null
                    ? 'neutral'
                    : history.compliance.completionRate >= 95
                      ? 'ok'
                      : history.compliance.completionRate >= 80
                        ? 'warn'
                        : 'alarm'
                }
              />
              <Stat
                label="Abnormalities"
                value={history.compliance.abnormalities}
                hint={`Last ${history.windowDays} days`}
                tone={history.compliance.abnormalities > 0 ? 'warn' : 'ok'}
              />
              <Stat
                label="Open problems"
                value={history.problems.open}
                hint={`${history.problems.closed} closed all-time`}
                tone={history.problems.open > 0 ? 'warn' : 'ok'}
              />
              <Stat label="OPLs" value={history.opls.length} hint="Lessons for this machine" />
            </div>

            {history.recurringIssues.length > 0 ? (
              <Card>
                <CardHeader
                  title="Recurring issues"
                  subtitle="Components tagged more than once — candidates for an AM Step 2 countermeasure or an OPL"
                />
                <ul className="divide-y divide-steel-100">
                  {history.recurringIssues.map((issue) => (
                    <li key={issue.componentId ?? issue.componentName} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-steel-900">{issue.componentName}</p>
                        <p className="text-xs text-steel-500">Last seen {formatDate(issue.lastSeen)}</p>
                      </div>
                      <Badge tone="warn" icon={AlertTriangle}>
                        {issue.occurrences}×
                      </Badge>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader title="Recent checklists" />
                {history.recentRuns.length === 0 ? (
                  <EmptyState
                    icon={ClipboardList}
                    title="No checklists yet"
                    message="Once operators start performing CILT checks on this machine they will appear here."
                  />
                ) : (
                  <ul className="divide-y divide-steel-100">
                    {history.recentRuns.map((run) => (
                      <li key={run.id}>
                        <ListRow onClick={() => navigate(`/checklists/${run.id}/review`)}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-steel-900">
                                {run.templateName}
                              </p>
                              <p className="text-xs text-steel-500">
                                {run.operatorName}
                                {run.shift ? ` · Shift ${run.shift}` : ''} ·{' '}
                                {formatDateTime(run.startedAt)}
                              </p>
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1">
                              <StatusPill tone={runStatusTone(run.status)} label={humanise(run.status)} />
                              {run.abnormalCount > 0 ? (
                                <span className="text-xs font-bold text-warn-700">
                                  {run.abnormalCount} abnormal
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </ListRow>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card>
                <CardHeader title="Recent problems" />
                {history.recentProblems.length === 0 ? (
                  <EmptyState
                    icon={Tag}
                    title="No problems raised"
                    message="Nothing has been tagged on this machine."
                  />
                ) : (
                  <ul className="divide-y divide-steel-100">
                    {history.recentProblems.map((problem) => (
                      <li key={problem.id}>
                        <ListRow onClick={() => navigate(`/problems/${problem.id}`)}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-steel-900">
                                {problem.tagNo} · {problem.title}
                              </p>
                              <p className="text-xs text-steel-500">
                                {problem.componentName ?? 'Machine level'} ·{' '}
                                {formatDate(problem.detectedAt)}
                              </p>
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1">
                              <StatusPill
                                tone={problemStatusTone(problem.status)}
                                label={humanise(problem.status)}
                              />
                            </div>
                          </div>
                        </ListRow>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            {history.opls.length > 0 ? (
              <Card>
                <CardHeader title="One-Point Lessons for this machine" />
                <ul className="divide-y divide-steel-100">
                  {history.opls.map((opl) => (
                    <li key={opl.id}>
                      <ListRow onClick={() => navigate(`/opl/${opl.id}`)}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-semibold text-steel-900">
                            {opl.oplNo} · {opl.title}
                          </p>
                          <StatusPill tone={oplStatusTone(opl.status)} label={humanise(opl.status)} />
                        </div>
                        <p className="text-xs text-steel-500">{humanise(opl.oplType)}</p>
                      </ListRow>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </>
        ) : null}

        <ComponentsCard machineId={id} canManage={canManage} />

        <Card>
          <CardHeader title="Machine details" />
          <dl className="px-4 py-2">
            <DetailRow label="Machine ID">{machine.code}</DetailRow>
            <DetailRow label="Type">{machine.machineType}</DetailRow>
            <DetailRow label="Manufacturer">{machine.manufacturer}</DetailRow>
            <DetailRow label="Model">{machine.model}</DetailRow>
            <DetailRow label="Serial number">{machine.serialNo}</DetailRow>
            <DetailRow label="Location">{machine.location}</DetailRow>
            <DetailRow label="Commissioned">{formatDate(machine.commissionDate)}</DetailRow>
            <DetailRow label="Description">{machine.description}</DetailRow>
          </dl>
        </Card>
      </div>

      <QrModal machineId={id} code={machine.code} open={qrOpen} onClose={() => setQrOpen(false)} />

      {editOpen ? (
        <EquipmentFormModal
          existing={machine}
          onClose={() => setEditOpen(false)}
          onSaved={() => setEditOpen(false)}
        />
      ) : null}

      {retireOpen ? (
        <Modal
          open
          onClose={() => setRetireOpen(false)}
          title={`Retire ${machine.code}?`}
          footer={
            <div className="flex gap-2">
              <Button variant="secondary" fullWidth onClick={() => setRetireOpen(false)}>
                Cancel
              </Button>
              <Button fullWidth loading={retire.isPending} onClick={() => retire.mutate()}>
                Retire equipment
              </Button>
            </div>
          }
        >
          <div className="space-y-3">
            <Callout tone="warn" title="Nothing is deleted">
              Retiring marks the equipment out of service and switches off its checklist
              schedules, so nothing new becomes due. Every run, Problem Tag and OPL it
              collected stays exactly as it is and remains in the analytics.
            </Callout>
            <p className="text-sm text-steel-700">
              ODR refuses to retire equipment with checklists still in progress — finish or
              cancel those first.
            </p>
            {retire.isError ? (
              <Callout tone="alarm">
                {retire.error instanceof ApiError
                  ? retire.error.message
                  : 'That equipment could not be retired.'}
              </Callout>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function QrModal({
  machineId,
  code,
  open,
  onClose,
}: {
  machineId: string;
  code: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data } = useQuery({
    queryKey: ['machine', machineId, 'qr'],
    queryFn: () => api.get<{ url: string; svg: string }>(`/api/machines/${machineId}/qr`),
    enabled: open,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`QR label — ${code}`}
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Close
          </Button>
          <Button fullWidth onClick={() => window.print()}>
            Print
          </Button>
        </div>
      }
    >
      {data ? (
        <div className="text-center">
          <div
            className="mx-auto w-56 [&>svg]:h-auto [&>svg]:w-full"
            // The SVG is generated server-side by the qrcode library from a URL
            // we control — no user content reaches this markup.
            dangerouslySetInnerHTML={{ __html: data.svg }}
          />
          <p className="mt-4 text-lg font-black text-steel-900">{code}</p>
          <p className="mt-1 font-mono text-xs break-all text-steel-500">{data.url}</p>
          <p className="mt-4 text-left text-xs text-steel-500">
            Print this and fix it to the machine. If a label is damaged or has been
            photographed by someone who should not have it, reissue it from machine
            administration — the old label stops working immediately.
          </p>
        </div>
      ) : (
        <Spinner label="Generating label" />
      )}
    </Modal>
  );
}
